'use client'

import { useCallback, useEffect, useMemo, useState } from 'react'

import { useGatewayTimeRangeFilterUiConfig } from '../../../hooks/useGatewayTimeRangeFilterUiConfig'
import type { ServiceMapTimeRangeFilterValue } from '../../../types/serviceMapTimeRangeFilterValue.type'
import { resolveCustomRangeFromTimeRangeSelection } from '../../../utils/resolveCustomRangeFromTimeRangeSelection'
import { TIME_RANGE_CUSTOM_VALUE } from '../time-range.const'
import type { ServiceMapCustomRange, ServiceMapTimeRangeFilterProps } from '../time-range.types'
import { combineDateTime } from '../utils/combineDateTime'
import { isValidCustomRangeForApply } from '../utils/isValidCustomRangeForApply'
import { parsePopoverPlacement } from '../utils/parsePopoverPlacement'
import { splitOptionsIntoTwoColumns } from '../utils/splitOptionsIntoTwoColumns'

import { useCustomRangeState } from './useCustomRangeState'

import styles from '../TimeRangeFilter.module.scss'

export function useTimeRangeFilter({
  className,
  value,
  defaultValue,
  customValue,
  customRange,
  defaultCustomRange,
  options: optionsProp,
  labels: labelsProp,
  disabled = false,
  placement = 'bottom-start',
  hour12 = true,
  dateFormat,
  onApply,
  onOpenChange,
}: ServiceMapTimeRangeFilterProps) {
  const { options: defaultOptions, labels: defaultLabels } = useGatewayTimeRangeFilterUiConfig()
  const resolvedOptions = optionsProp ?? defaultOptions
  const resolvedLabels = labelsProp ?? defaultLabels

  const isControlled = value !== undefined
  const fallbackValue = resolvedOptions[0]?.value ?? TIME_RANGE_CUSTOM_VALUE
  const [internalValue, setInternalValue] = useState<ServiceMapTimeRangeFilterValue>(
    defaultValue ?? (fallbackValue as ServiceMapTimeRangeFilterValue)
  )
  const [isOpen, setIsOpen] = useState(false)

  const appliedValue = isControlled ? (value as ServiceMapTimeRangeFilterValue) : internalValue
  const [pendingValue, setPendingValue] = useState<ServiceMapTimeRangeFilterValue>(appliedValue)

  const isCustomRangeControlled = customRange !== undefined
  const [internalCustomRange, setInternalCustomRange] = useState<ServiceMapCustomRange>(
    defaultCustomRange ?? { startDate: null, startTime: null, endDate: null, endTime: null }
  )
  const appliedCustomRange = isCustomRangeControlled ? (customRange as ServiceMapCustomRange) : internalCustomRange

  const {
    range: pendingRange,
    setRange,
    setStartDate,
    setStartTime,
    setEndDate,
    setEndTime,
  } = useCustomRangeState(appliedCustomRange)

  const selectedOption = useMemo(
    () => resolvedOptions.find((option) => option.value === appliedValue),
    [resolvedOptions, appliedValue]
  )

  const triggerLabel = useMemo(() => {
    if (appliedValue === TIME_RANGE_CUSTOM_VALUE) {
      return customValue && customValue.length > 0 ? customValue : resolvedLabels.triggerCustomFallback
    }
    return selectedOption?.label ?? resolvedLabels.triggerCustomFallback
  }, [appliedValue, customValue, resolvedLabels.triggerCustomFallback, selectedOption])

  const gridColumns = useMemo(() => splitOptionsIntoTwoColumns(resolvedOptions), [resolvedOptions])

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (disabled) return
      setIsOpen(nextOpen)
      onOpenChange?.(nextOpen)
    },
    [disabled, onOpenChange]
  )

  const handlePendingValueChange = useCallback(
    (next: ServiceMapTimeRangeFilterValue) => {
      if (next === TIME_RANGE_CUSTOM_VALUE && pendingValue !== TIME_RANGE_CUSTOM_VALUE) {
        const migrated = resolveCustomRangeFromTimeRangeSelection(pendingValue, pendingRange)
        if (migrated) {
          setRange(migrated)
        }
      }
      setPendingValue(next)
    },
    [pendingRange, pendingValue, setRange]
  )

  const handleCancel = useCallback(() => {
    setPendingValue(appliedValue)
    setRange(appliedCustomRange)
    handleOpenChange(false)
  }, [appliedCustomRange, appliedValue, handleOpenChange, setRange])

  const canApplyCustom = useMemo(() => {
    if (pendingValue !== TIME_RANGE_CUSTOM_VALUE) return true
    return isValidCustomRangeForApply(pendingRange)
  }, [pendingRange, pendingValue])

  const handleApply = useCallback(() => {
    if (!isControlled) setInternalValue(pendingValue)

    if (pendingValue === TIME_RANGE_CUSTOM_VALUE) {
      const startAt = combineDateTime(pendingRange.startDate, pendingRange.startTime)
      const endAt = combineDateTime(pendingRange.endDate, pendingRange.endTime)
      if (!startAt || !endAt) return

      if (!isCustomRangeControlled) setInternalCustomRange(pendingRange)
      onApply?.(pendingValue, { startAt, endAt })
    } else {
      onApply?.(pendingValue, null)
    }

    handleOpenChange(false)
  }, [handleOpenChange, isControlled, isCustomRangeControlled, onApply, pendingRange, pendingValue])

  const showCustomSection = pendingValue === TIME_RANGE_CUSTOM_VALUE

  const panelClass = useMemo(() => {
    const classes = [styles.panel]
    if (className) classes.push(className)
    return classes.join(' ')
  }, [className])

  const { side, align } = useMemo(() => parsePopoverPlacement(placement), [placement])

  const sectionLabels = useMemo(
    () => ({
      startDate: resolvedLabels.startDate,
      endDate: resolvedLabels.endDate,
      startTime: resolvedLabels.startTime,
      endTime: resolvedLabels.endTime,
      startDateAriaLabel: resolvedLabels.startDateAriaLabel,
      startTimeAriaLabel: resolvedLabels.startTimeAriaLabel,
      endDateAriaLabel: resolvedLabels.endDateAriaLabel,
      endTimeAriaLabel: resolvedLabels.endTimeAriaLabel,
    }),
    [resolvedLabels]
  )

  useEffect(() => {
    if (isOpen) {
      setPendingValue(appliedValue)
      setRange(appliedCustomRange)
    }
  }, [isOpen, appliedValue, appliedCustomRange, setRange])

  return {
    align,
    canApplyCustom,
    dateFormat,
    gridColumns,
    handleApply,
    handleCancel,
    handleOpenChange,
    handlePendingValueChange,
    hour12,
    isOpen,
    panelClass,
    pendingRange,
    pendingValue,
    resolvedLabels,
    sectionLabels,
    setPendingValue,
    setEndDate,
    setEndTime,
    setStartDate,
    setStartTime,
    showCustomSection,
    side,
    triggerLabel,
  }
}
